import { useTranslation } from 'react-i18next';

import { Box, Table, TableBody, TableCell, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import { useSelector } from 'hooks';
import { Icon } from '@sdb/planning-ui';

type Props = {
  periods: {
    start: string;
    end: string;
    dutyHourType: { description: string };
  }[];
  warnings?: string[];
  onMouseOver?: () => void;
  isIncludedInKidsRatio?: boolean;
  multiplePlanUnitsSelected?: boolean;
  address?: string;
  organizationName?: string;
  functionDescription?: string;
};

export default function DutyTooltipBody({
  isIncludedInKidsRatio,
  periods,
  onMouseOver,
  multiplePlanUnitsSelected,
  address,
  organizationName,
  functionDescription
}: Props) {
  const { t } = useTranslation('common');
  const { hasKidsPlanAccess } = useSelector(({ grants }) => grants);

  return (
    <Table
      onMouseOver={onMouseOver}
      size="small"
      sx={{
        'tr:last-child td': { border: 0 },
        th: { fontFamily: 'SourceSansPro-Regular' },
        'tr td:first-of-type': { fontFamily: 'SourceSansPro-Bold' }
      }}
    >
      <TableBody>
        {periods && periods.length > 0 && (
          <TableRow sx={{ verticalAlign: 'top' }}>
            <TableCell colSpan={2}>
              <Box
                height="100%"
                display="flex"
                justifyContent="start"
                alignItems="start"
              >
                {t('period')}
              </Box>
            </TableCell>
            <TableCell>
              {[...periods]
                .sort(
                  (a, b) =>
                    new Date(a.start).getTime() - new Date(b.start).getTime()
                )
                .map((period, idx) => (
                  <Box key={idx} display="flex" flexDirection="column">
                    {dayjs(period.start).format('HH:mm')} -{' '}
                    {dayjs(period.end).format('HH:mm')} {t('hour')}
                  </Box>
                ))}
            </TableCell>
          </TableRow>
        )}
        {periods[0].dutyHourType.description && (
          <TableRow sx={{ verticalAlign: 'top' }}>
            <TableCell colSpan={2}>{t('dutyHourType')}</TableCell>
            <TableCell>
              {periods.map(
                (period, idx) =>
                  (idx === 0 ||
                    period.dutyHourType.description !==
                      periods[idx - 1].dutyHourType.description) && (
                    <Box
                      key={idx}
                      display="flex"
                      flexDirection="column"
                      textTransform="capitalize"
                    >
                      {period.dutyHourType.description}
                    </Box>
                  )
              )}
            </TableCell>
          </TableRow>
        )}
        {functionDescription && (
          <>
            <TableRow>
              <TableCell colSpan={2}>
                <Box display="flex" alignItems="center">
                  {t('function')}
                </Box>
              </TableCell>
              <TableCell>{functionDescription}</TableCell>
            </TableRow>
          </>
        )}
        {multiplePlanUnitsSelected && address != undefined && (
          <>
            <TableRow>
              <TableCell colSpan={2}>
                <Box display="flex" alignItems="center">
                  {t('planUnit')}
                </Box>
              </TableCell>
              <TableCell>{organizationName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={2}>
                <Box display="flex" alignItems="center">
                  {t('activityPlanning.location')}
                </Box>
              </TableCell>
              <TableCell>{address}</TableCell>
            </TableRow>
          </>
        )}
        {!isIncludedInKidsRatio && hasKidsPlanAccess && (
          <TableRow>
            <TableCell colSpan={2}>
              <Box display="flex" alignItems="center">
                <Icon
                  type="triangle-exclamation"
                  fontSize="small"
                  color="warning"
                />
              </Box>
            </TableCell>
            <TableCell>{t('notIncludedInKidsRatio')}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
